import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiChevronRight, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './DataTable.css';

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => ReactNode;
  width?: string;
}

interface DataTableProps<T extends { id: string }> {
  columns: Column<T>[];
  data: T[];
  isLoading: boolean;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onCreate?: () => void;
  emptyMessage?: string;
  createLabel?: string;
  // Opt-in expandable rows. When `renderExpanded` is provided, each row gets a
  // chevron toggle in a leading cell; expanding renders `renderExpanded(item)`
  // in a full-width row underneath. `isRowExpandable` can suppress the toggle
  // for rows with nothing to show. Tables that omit these render exactly as
  // before (flat).
  renderExpanded?: (item: T) => ReactNode;
  isRowExpandable?: (item: T) => boolean;
}

function DataTable<T extends { id: string }>({
  columns,
  data,
  isLoading,
  onEdit,
  onDelete,
  onCreate,
  emptyMessage,
  createLabel,
  renderExpanded,
  isRowExpandable,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const hasActions = onEdit || onDelete;
  const expandable = !!renderExpanded;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="data-table-loading">
        <div className="data-table-skeleton" />
        <div className="data-table-skeleton" />
        <div className="data-table-skeleton" />
      </div>
    );
  }

  // Total column count for the full-width expanded row's colspan.
  const colSpan = columns.length + (expandable ? 1 : 0) + (hasActions ? 1 : 0);

  return (
    <div className="data-table-wrapper">
      {onCreate && (
        <div className="data-table-toolbar">
          <button className="management-btn management-btn--primary" onClick={onCreate}>
            + {createLabel ?? t('management.create')}
          </button>
        </div>
      )}
      {data.length === 0 ? (
        <p className="data-table-empty">{emptyMessage ?? t('management.noData')}</p>
      ) : (
        <div className="data-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {expandable && <th className="data-table-expand-header" aria-hidden="true" />}
                {columns.map((col) => (
                  <th key={col.key} style={col.width ? { width: col.width } : undefined}>
                    {col.header}
                  </th>
                ))}
                {hasActions && (
                  <th className="data-table-actions-header">{t('management.actions')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const canExpand = expandable && (isRowExpandable ? isRowExpandable(item) : true);
                const isExpanded = expandedIds.has(item.id);
                return (
                  <Fragment key={item.id}>
                    <tr className={isExpanded ? 'data-table-row--expanded' : undefined}>
                      {expandable && (
                        <td className="data-table-expand-cell">
                          {canExpand && (
                            <button
                              className="data-table-expand-btn"
                              onClick={() => toggleRow(item.id)}
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded ? t('management.collapse') : t('management.expand')
                              }
                              title={isExpanded ? t('management.collapse') : t('management.expand')}
                            >
                              {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                            </button>
                          )}
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.key}>
                          {col.render
                            ? col.render(item)
                            : String((item as Record<string, unknown>)[col.key] ?? '')}
                        </td>
                      ))}
                      {hasActions && (
                        <td className="data-table-actions">
                          {onEdit && (
                            <button
                              className="data-table-action-btn"
                              onClick={() => onEdit(item)}
                              aria-label={t('management.edit')}
                              title={t('management.edit')}
                            >
                              <FiEdit2 />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              className="data-table-action-btn data-table-action-btn--danger"
                              onClick={() => onDelete(item)}
                              aria-label={t('management.delete')}
                              title={t('management.delete')}
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    {canExpand && isExpanded && (
                      <tr className="data-table-expanded-row">
                        <td colSpan={colSpan} className="data-table-expanded-cell">
                          {renderExpanded(item)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataTable;
